import { useTranslation } from 'react-i18next';
import { Card, Input } from '../common';
import { useProductionStore } from '../../stores/productionStore';
import { validateNumeric, validatePositive } from '../../lib/validation/validators';
import { useUnitStore, KG_TO_LB } from '../../stores/unitStore';

export function HarvestDetailsForm() {
  const { t } = useTranslation();
  const { weightUnit } = useUnitStore();
  const { harvestEvents, updateHarvestEvent, calculateHarvestTotals } = useProductionStore();

  const handleValueChange = (
    index: number,
    field: 'count' | 'averageWeight' | 'totalWeight',
    value: string
  ) => {
    // Boş string'e izin ver (kullanıcı silme yapıyor olabilir)
    if (value === '') {
      updateHarvestEvent(index, { [field]: '' as any });
      return;
    }

    // Sayı parse edilebiliyorsa kaydet (validasyon blur'da yapılacak)
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateHarvestEvent(index, { [field]: numValue });
    }
  };

  const totals = calculateHarvestTotals();

  const getFieldError = (value: number): string | undefined => {
    if (value === 0) return undefined;
    
    if (!validateNumeric(value)) {
      return t('productionData.validNumber');
    }
    
    if (!validatePositive(value)) {
      return t('productionData.positiveNumber');
    }
    
    return undefined;
  };

  return (
    <Card title={t('harvestDetails.title')}>
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                {t('harvestDetails.type')}
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                {t('harvestDetails.count')}
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                {t('harvestDetails.averageWeight', { unit: weightUnit })}
              </th>
              <th className="px-2 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                {t('harvestDetails.totalWeight', { unit: weightUnit })}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {/* Thinning Row */}
            <tr>
              <td className="px-2 py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('harvestDetails.thinning')}
              </td>
              <td className="px-2 py-3">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={harvestEvents[0].count ?? ''}
                  onChange={(e) => handleValueChange(0, 'count', e.target.value)}
                  error={getFieldError(harvestEvents[0].count)}
                  className="w-20 sm:w-full min-w-[80px]"
                  placeholder="0"
                />
              </td>
              <td className="px-2 py-3">
                <Input
                  type="number"
                  min="0"
                  step="0.001"
                  value={harvestEvents[0].averageWeight ?? ''}
                  onChange={(e) => handleValueChange(0, 'averageWeight', e.target.value)}
                  error={getFieldError(harvestEvents[0].averageWeight)}
                  className="w-24 sm:w-full min-w-[96px]"
                  placeholder="0.000"
                />
              </td>
              <td className="px-2 py-3 text-right">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={harvestEvents[0].totalWeight ?? ''}
                  onChange={(e) => handleValueChange(0, 'totalWeight', e.target.value)}
                  error={getFieldError(harvestEvents[0].totalWeight)}
                  className="w-24 sm:w-full min-w-[96px] text-right"
                  placeholder="0.00"
                />
              </td>
            </tr>

            {/* Final Harvest Row */}
            <tr>
              <td className="px-2 py-3 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('harvestDetails.finalHarvest')}
              </td>
              <td className="px-2 py-3">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={harvestEvents[1].count ?? ''}
                  onChange={(e) => handleValueChange(1, 'count', e.target.value)}
                  error={getFieldError(harvestEvents[1].count)}
                  className="w-20 sm:w-full min-w-[80px]"
                  placeholder="0"
                  required
                />
              </td>
              <td className="px-2 py-3">
                <Input
                  type="number"
                  min="0"
                  step="0.001"
                  value={harvestEvents[1].averageWeight ?? ''}
                  onChange={(e) => handleValueChange(1, 'averageWeight', e.target.value)}
                  error={getFieldError(harvestEvents[1].averageWeight)}
                  className="w-24 sm:w-full min-w-[96px]"
                  placeholder="0.000"
                  required
                />
              </td>
              <td className="px-2 py-3 text-right">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={harvestEvents[1].totalWeight ?? ''}
                  onChange={(e) => handleValueChange(1, 'totalWeight', e.target.value)}
                  error={getFieldError(harvestEvents[1].totalWeight)}
                  className="w-24 sm:w-full min-w-[96px] text-right"
                  placeholder="0.00"
                  required
                />
              </td>
            </tr>

            {/* Totals Row */}
            <tr className="bg-gray-50 dark:bg-gray-800 font-semibold">
              <td className="px-2 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                {t('harvestDetails.total')}
              </td>
              <td className="px-2 py-3 text-xs sm:text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                {totals.totalCount.toLocaleString('tr-TR')}
              </td>
              <td className="px-2 py-3 text-xs sm:text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                {weightUnit === 'lb' ? ((totals.averageWeight || 0) * KG_TO_LB).toFixed(3) : (totals.averageWeight || 0).toFixed(3)} {weightUnit}
              </td>
              <td className="px-2 py-3 text-xs sm:text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap text-right">
                {weightUnit === 'lb' ? ((totals.totalWeight || 0) * KG_TO_LB).toFixed(3) : (totals.totalWeight || 0).toFixed(3)} {weightUnit}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Info Box */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>{t('harvestDetails.smartCalculationTitle')}</strong> {t('harvestDetails.smartCalculation')}
              <br />
              <strong>{t('harvestDetails.note')}</strong> {t('harvestDetails.thinningOptional')}
              <br />
              <strong>{t('harvestDetails.note')}</strong> {t('harvestDetails.decimalNote')}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
